import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import CustomButton from 'lib/designSystem/Button';
import { ApprovalRejectionDialogProps } from './types';

const ApprovalRejectionDialog: React.FC<ApprovalRejectionDialogProps> = ({
  open,
  type,
  onConfirm,
  onCancel,
  setOpen,
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setReason('');
  };

  const handleConfirm = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    try {
      await onConfirm(reason);
      handleClose();
    } catch (error) {
      console.error('Error al procesar la acción:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    handleClose();
  };

  const isApprove = type === 'approve';
  const title = isApprove ? 'Aprobar Solicitud' : 'Rechazar Solicitud';
  const description = isApprove
    ? 'Confirma que la solicitud cumple con todos los criterios establecidos.'
    : 'Indica el motivo por el cual rechazas esta solicitud.';
  const buttonColor = isApprove ? 'success' : 'error';
  const buttonText = isApprove ? 'Aprobar' : 'Rechazar';
  const icon = isApprove ? <CheckCircle /> : <Cancel />;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="approval-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        id="approval-dialog-title"
        sx={{
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: isApprove
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(244, 67, 54, 0.1)',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: '1.2rem',
              color: isApprove ? 'success.main' : 'error.main',
            },
          })}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          {description}
        </Typography>

        <TextField
          autoFocus
          label="Razón"
          type="text"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            isApprove
              ? 'Describe por qué apruebas esta solicitud...'
              : 'Describe por qué rechazas esta solicitud...'
          }
          error={!reason.trim()}
          helperText={!reason.trim() ? 'La razón es requerida' : ''}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          onClick={handleCancel}
          color="secondary"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleConfirm}
          color={buttonColor as any}
          disabled={loading || !reason.trim()}
        >
          {loading ? 'Procesando...' : buttonText}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalRejectionDialog;
